/**
 * Helpers for playbooks and plans (Events hub Stage 2): names and colours (the server's lookup
 * names are used when present), timing sentences, the approve summary, record paths for created
 * work and the work link types shown on the Connected tab.
 *
 * Stage 3: budget from the plan's project, draft purchase request and announcement items (and
 * their link types 24 to 26), per type date labels and the "no project yet" budget hint.
 */

import {
  EventAssigneeStrategyIds,
  EventLinkTypeIds,
  EventPlanAnchorIds,
  EventPlanItemStatusIds,
  EventPlanStatusIds,
  EventPlaybookItemTypeIds,
  type EventPlanItemView,
  type EventPlanView,
} from '@actschurch/ccms-sdk';
import { ANNOUNCEMENTS_ROUTES, MEETINGS_ROUTES, PROJECTS_ROUTES, TASKS_ROUTES } from '@/config/routes';
import { formatDateTime, formatNumber, formatShortDate, parseIso } from './eventsUtils';

const IT = EventPlaybookItemTypeIds;

// ---------------------------------------------------------------- item types

export const ITEM_TYPE_NAMES: Record<number, string> = {
  [IT.Task]: 'Task',
  [IT.ProjectFromTemplate]: 'Project from template',
  [IT.Meeting]: 'Meeting',
  [IT.Booking]: 'Booking',
  [IT.BudgetFromProject]: 'Budget',
  [IT.PurchaseRequest]: 'Purchase request',
  [IT.Announcement]: 'Announcement',
};

/** Longer labels for the "Creates" picker. */
const ITEM_TYPE_OPTION_LABELS: Record<number, string> = {
  ...ITEM_TYPE_NAMES,
  [IT.BudgetFromProject]: "Budget from the plan's project",
  [IT.PurchaseRequest]: 'Purchase request (draft)',
};

export const ITEM_TYPE_COLORS: Record<number, string> = {
  [IT.Task]: 'blue',
  [IT.ProjectFromTemplate]: 'violet',
  [IT.Meeting]: 'teal',
  [IT.Booking]: 'orange',
  [IT.BudgetFromProject]: 'green',
  [IT.PurchaseRequest]: 'yellow',
  [IT.Announcement]: 'indigo',
};

/** Singular and plural nouns for the approve summary ("6 tasks, 1 meeting and 1 project"). */
const ITEM_TYPE_NOUNS: Record<number, [string, string]> = {
  [IT.Task]: ['task', 'tasks'],
  [IT.ProjectFromTemplate]: ['project', 'projects'],
  [IT.Meeting]: ['meeting', 'meetings'],
  [IT.Booking]: ['booking', 'bookings'],
  [IT.BudgetFromProject]: ['budget', 'budgets'],
  [IT.PurchaseRequest]: ['purchase request', 'purchase requests'],
  [IT.Announcement]: ['announcement', 'announcements'],
};

/** Display order of item types in summaries. */
const SUMMARY_ORDER: number[] = [
  IT.Task,
  IT.Meeting,
  IT.ProjectFromTemplate,
  IT.Booking,
  IT.BudgetFromProject,
  IT.PurchaseRequest,
  IT.Announcement,
];

export const ITEM_TYPE_OPTIONS = [
  IT.Task,
  IT.ProjectFromTemplate,
  IT.Meeting,
  IT.Booking,
  IT.BudgetFromProject,
  IT.PurchaseRequest,
  IT.Announcement,
].map((id) => ({
  value: String(id),
  label: ITEM_TYPE_OPTION_LABELS[id],
}));

export function itemTypeName(id: number, serverName?: string | null): string {
  return ITEM_TYPE_NAMES[id] ?? serverName ?? `Type ${id}`;
}

/** "task", "project", "purchase request" (for "Open the ..." links). */
export function itemTypeNoun(id: number): string {
  return ITEM_TYPE_NOUNS[id]?.[0] ?? 'record';
}

/** Stage 3 item types: no start time, length or priority; budget and announcement have no assignee. */
export const isStage3ItemType = (id: number) => id === IT.BudgetFromProject || id === IT.PurchaseRequest || id === IT.Announcement;

// ---------------------------------------------------------------- plan + item statuses

const PS = EventPlanStatusIds;
export const PLAN_STATUS_NAMES: Record<number, string> = {
  [PS.Draft]: 'Draft',
  [PS.Approved]: 'Approved',
  [PS.Executing]: 'Creating work',
  [PS.Completed]: 'Completed',
  [PS.Partial]: 'Partly created',
  [PS.Cancelled]: 'Cancelled',
  [PS.OutOfDate]: 'Out of date',
};
export const PLAN_STATUS_COLORS: Record<number, string> = {
  [PS.Draft]: 'gray',
  [PS.Approved]: 'blue',
  [PS.Executing]: 'cyan',
  [PS.Completed]: 'teal',
  [PS.Partial]: 'yellow',
  [PS.Cancelled]: 'gray',
  [PS.OutOfDate]: 'orange',
};

const IS = EventPlanItemStatusIds;
export const ITEM_STATUS_NAMES: Record<number, string> = {
  [IS.Proposed]: 'Proposed',
  [IS.Queued]: 'Queued',
  [IS.Creating]: 'Creating',
  [IS.Created]: 'Created',
  [IS.Failed]: 'Failed',
  [IS.Skipped]: 'Skipped',
};
export const ITEM_STATUS_COLORS: Record<number, string> = {
  [IS.Proposed]: 'gray',
  [IS.Queued]: 'blue',
  [IS.Creating]: 'cyan',
  [IS.Created]: 'teal',
  [IS.Failed]: 'red',
  [IS.Skipped]: 'gray',
};

export const isDraftPlan = (p: Pick<EventPlanView, 'statusID'>) => p.statusID === PS.Draft;
export const isCancelledPlan = (p: Pick<EventPlanView, 'statusID'>) => p.statusID === PS.Cancelled;
export const isOutOfDatePlan = (p: Pick<EventPlanView, 'statusID'>) => p.statusID === PS.OutOfDate;

/** Queued or being created right now. */
export const isItemInFlight = (i: Pick<EventPlanItemView, 'statusID'>) => i.statusID === IS.Queued || i.statusID === IS.Creating;

/** The plan still has work on its way (poll while true). */
export function planIsLive(p: EventPlanView): boolean {
  if (p.items.some(isItemInFlight)) return true;
  // Approved and Executing are moments before items are queued or after the last one is claimed.
  return p.statusID === PS.Approved || p.statusID === PS.Executing;
}

// ---------------------------------------------------------------- timing + assignees

export const ANCHOR_OPTIONS = [
  { value: String(EventPlanAnchorIds.EventStart), label: 'Event start' },
  { value: String(EventPlanAnchorIds.EventEnd), label: 'Event end' },
];

/** "3 days before the event starts", "On the day the event ends", "1 day after the event starts". */
export function describeOffset(offsetDays: number, anchorID: number): string {
  const anchor = anchorID === EventPlanAnchorIds.EventEnd ? 'the event ends' : 'the event starts';
  const n = Math.abs(Math.trunc(offsetDays || 0));
  if (n === 0) return `On the day ${anchor}`;
  const days = `${formatNumber(n)} ${n === 1 ? 'day' : 'days'}`;
  return offsetDays < 0 ? `${days} before ${anchor}` : `${days} after ${anchor}`;
}

const AS = EventAssigneeStrategyIds;
export const STRATEGY_NAMES: Record<number, string> = {
  [AS.Designation]: 'Everyone in a designation',
  [AS.LeadDepartment]: 'The lead department',
  [AS.Coordinator]: 'The event coordinator',
  [AS.SpecificUser]: 'A specific person',
};
export const STRATEGY_OPTIONS = [AS.Coordinator, AS.Designation, AS.LeadDepartment, AS.SpecificUser].map((id) => ({
  value: String(id),
  label: STRATEGY_NAMES[id],
}));

/**
 * Stage 3: how a purchase request's department is chosen. The planner reads Lead department as the
 * event's lead department (else the item's department), Designation as that designation's
 * department and any other strategy (Coordinator here) as the item's own department.
 */
export const PURCHASE_STRATEGIES: number[] = [AS.LeadDepartment, AS.Coordinator, AS.Designation];
export const PURCHASE_STRATEGY_NAMES: Record<number, string> = {
  [AS.LeadDepartment]: "The event's lead department",
  [AS.Coordinator]: 'A specific department',
  [AS.Designation]: "A designation's department",
};
export const PURCHASE_STRATEGY_OPTIONS = PURCHASE_STRATEGIES.map((id) => ({ value: String(id), label: PURCHASE_STRATEGY_NAMES[id] }));

/** Half-hour start times for the playbook editor; empty = the server default (09:00). */
export const START_TIME_OPTIONS = (() => {
  const out: { value: string; label: string }[] = [];
  for (let h = 6; h <= 21; h++) {
    for (const m of [0, 30]) {
      const v = `${String(h).padStart(2, '0')}:${m === 0 ? '00' : '30'}`;
      out.push({ value: v, label: v });
    }
  }
  return out;
})();

/** Tokens a title or description template may use. */
export const TITLE_TOKENS = [
  { token: '{EventName}', label: 'Event name' },
  { token: '{EventDate}', label: 'Event date' },
  { token: '{Campus}', label: 'Campus' },
];

// ---------------------------------------------------------------- plan item dates

export type PlanDateField = 'startUtc' | 'dueUtc' | 'endUtc';

export const DATE_FIELD_LABELS: Record<PlanDateField, string> = {
  startUtc: 'Starts',
  dueUtc: 'Due',
  endUtc: 'Ends',
};

/**
 * Tasks and projects have a due date; meetings and bookings run from a start to an end. Stage 3:
 * a budget covers a period, a purchase request is needed by a date and an announcement is active
 * from a date until the event ends.
 */
const DEFAULT_DATE_FIELDS: Record<number, PlanDateField[]> = {
  [IT.Task]: ['startUtc', 'dueUtc'],
  [IT.ProjectFromTemplate]: ['startUtc', 'dueUtc'],
  [IT.Meeting]: ['startUtc', 'endUtc'],
  [IT.Booking]: ['startUtc', 'endUtc'],
  [IT.BudgetFromProject]: ['startUtc', 'endUtc'],
  [IT.PurchaseRequest]: ['dueUtc'],
  [IT.Announcement]: ['startUtc', 'endUtc'],
};

const TYPE_DATE_LABELS: Record<number, Partial<Record<PlanDateField, string>>> = {
  [IT.BudgetFromProject]: { startUtc: 'Budget starts', endUtc: 'Budget ends' },
  [IT.PurchaseRequest]: { dueUtc: 'Needed by' },
  [IT.Announcement]: { startUtc: 'Active from', endUtc: 'Active until' },
};

/** Label of one date field for an item type ("Needed by", "Active from", "Due"). */
export function dateFieldLabel(itemTypeID: number, field: PlanDateField): string {
  return TYPE_DATE_LABELS[itemTypeID]?.[field] ?? DATE_FIELD_LABELS[field];
}

/** Date fields to show for an item: its type's defaults plus any other field the server filled. */
export function dateFieldsFor(item: Pick<EventPlanItemView, 'itemTypeID' | 'startUtc' | 'dueUtc' | 'endUtc'>): PlanDateField[] {
  const base = DEFAULT_DATE_FIELDS[item.itemTypeID] ?? ['startUtc', 'dueUtc', 'endUtc'];
  const all: PlanDateField[] = ['startUtc', 'dueUtc', 'endUtc'];
  return all.filter((f) => base.includes(f) || !!item[f]);
}

const shortDate = (iso?: string | null) => formatShortDate(parseIso(iso), '');

/**
 * One line for an item's dates: "Needed by 3 Oct 2026", "Active from 1 Oct 2026, 09:00 to
 * 12 Oct 2026, 18:00", "Budget period 1 Sept 2026 to 12 Oct 2026", else "Starts ... Due ...".
 */
export function describePlanItemDates(item: Pick<EventPlanItemView, 'itemTypeID' | 'startUtc' | 'dueUtc' | 'endUtc'>): string {
  const t = item.itemTypeID;
  if (t === IT.PurchaseRequest) {
    const needed = item.dueUtc ?? item.startUtc;
    if (needed) return `Needed by ${shortDate(needed)}`;
  }
  if (t === IT.Announcement && item.startUtc) {
    return item.endUtc
      ? `Active from ${formatDateTime(item.startUtc)} to ${formatDateTime(item.endUtc)}`
      : `Active from ${formatDateTime(item.startUtc)}`;
  }
  if (t === IT.BudgetFromProject && item.startUtc && item.endUtc) {
    return `Budget period ${shortDate(item.startUtc)} to ${shortDate(item.endUtc)}`;
  }
  return dateFieldsFor(item)
    .map((f) => (item[f] ? `${dateFieldLabel(t, f)} ${formatDateTime(item[f])}` : null))
    .filter((x): x is string => !!x)
    .join('. ');
}

// ---------------------------------------------------------------- Stage 3: budgets need the plan's project

/** The server's error on a budget item when the plan made no project ("This plan has no project yet"). */
export const isNoProjectError = (message?: string | null) => /no project yet/i.test(message ?? '');

/** The plan has a project item that will be (or was) created. */
export function planHasProjectItem(items: Pick<EventPlanItemView, 'itemTypeID' | 'isSelected' | 'statusID'>[]): boolean {
  return items.some(
    (i) => i.itemTypeID === IT.ProjectFromTemplate && (i.isSelected || i.statusID !== EventPlanItemStatusIds.Proposed) && i.statusID !== EventPlanItemStatusIds.Skipped
  );
}

/** The plan's project item has been created (a budget item can go ahead). */
export function planProjectCreated(items: Pick<EventPlanItemView, 'itemTypeID' | 'statusID'>[]): boolean {
  return items.some((i) => i.itemTypeID === IT.ProjectFromTemplate && i.statusID === EventPlanItemStatusIds.Created);
}

// ---------------------------------------------------------------- summaries

/** "a, b and c" (no Oxford comma). */
export function joinList(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** "6 tasks, 1 meeting and 1 project" for the items given. */
export function describeItemCounts(items: Pick<EventPlanItemView, 'itemTypeID'>[]): string {
  const counts = new Map<number, number>();
  items.forEach((i) => counts.set(i.itemTypeID, (counts.get(i.itemTypeID) ?? 0) + 1));
  const order = [...SUMMARY_ORDER, ...Array.from(counts.keys()).filter((k) => !SUMMARY_ORDER.includes(k))];
  const parts = order
    .filter((t) => (counts.get(t) ?? 0) > 0)
    .map((t) => {
      const n = counts.get(t) ?? 0;
      const [one, many] = ITEM_TYPE_NOUNS[t] ?? ['item', 'items'];
      return `${formatNumber(n)} ${n === 1 ? one : many}`;
    });
  return joinList(parts);
}

/** Count of items per status id. */
export function countByStatus(items: Pick<EventPlanItemView, 'statusID'>[]): Record<number, number> {
  const out: Record<number, number> = {};
  items.forEach((i) => {
    out[i.statusID] = (out[i.statusID] ?? 0) + 1;
  });
  return out;
}

// ---------------------------------------------------------------- created work

/**
 * Link types for work created in other apps (EventLink types 20 to 23, and Stage 3's 24 budget,
 * 25 purchase request and 26 announcement).
 */
export const WORK_LINK_TYPES: number[] = [
  EventLinkTypeIds.Task,
  EventLinkTypeIds.Project,
  EventLinkTypeIds.Meeting,
  EventLinkTypeIds.Booking,
  EventLinkTypeIds.Budget,
  EventLinkTypeIds.PurchaseRequest,
  EventLinkTypeIds.Announcement,
];

export const isWorkLinkType = (linkTypeId: number) => WORK_LINK_TYPES.includes(linkTypeId);

/** Work link types whose owning app has a by-ids call for live status (the others show their stored names). */
export const LIVE_WORK_LINK_TYPES: number[] = [EventLinkTypeIds.Task, EventLinkTypeIds.Project, EventLinkTypeIds.Meeting, EventLinkTypeIds.Booking];

/** Console page of a task, project, meeting, booking, budget, purchase request or announcement, by link type. */
export function workRecordPath(linkTypeId: number, id: number | string): string | null {
  switch (linkTypeId) {
    case EventLinkTypeIds.Task:
      return TASKS_ROUTES.DETAIL(String(id));
    case EventLinkTypeIds.Project:
      return PROJECTS_ROUTES.VIEW(id);
    case EventLinkTypeIds.Meeting:
      return MEETINGS_ROUTES.VIEW(id);
    case EventLinkTypeIds.Booking:
      return `/app/bookings/view/${id}`;
    case EventLinkTypeIds.Budget:
      return `/app/budgets/view/${id}`;
    case EventLinkTypeIds.PurchaseRequest:
      return `/app/procurements/requests/view/${id}`;
    case EventLinkTypeIds.Announcement:
      return ANNOUNCEMENTS_ROUTES.DETAIL(String(id));
    default:
      return null;
  }
}

/** Link type of the record a plan item creates. */
export function linkTypeForItemType(itemTypeId: number): number | null {
  switch (itemTypeId) {
    case IT.Task:
      return EventLinkTypeIds.Task;
    case IT.ProjectFromTemplate:
      return EventLinkTypeIds.Project;
    case IT.Meeting:
      return EventLinkTypeIds.Meeting;
    case IT.Booking:
      return EventLinkTypeIds.Booking;
    case IT.BudgetFromProject:
      return EventLinkTypeIds.Budget;
    case IT.PurchaseRequest:
      return EventLinkTypeIds.PurchaseRequest;
    case IT.Announcement:
      return EventLinkTypeIds.Announcement;
    default:
      return null;
  }
}

/** Console page of the record a created plan item points at. */
export function planItemRecordPath(item: Pick<EventPlanItemView, 'itemTypeID' | 'targetRecordId'>): string | null {
  if (item.targetRecordId == null) return null;
  const linkType = linkTypeForItemType(item.itemTypeID);
  return linkType == null ? null : workRecordPath(linkType, item.targetRecordId);
}

/** Rough colour for a status name from another app (each app has its own status table). */
export function workStatusColor(name?: string | null): string {
  const n = (name ?? '').toLowerCase();
  if (!n) return 'gray';
  if (/(complete|done|closed|finished|approved|confirmed|returned)/.test(n)) return 'teal';
  if (/(cancel|reject|declin|fail|overdue)/.test(n)) return 'red';
  if (/(hold|pending|wait|draft|request)/.test(n)) return 'yellow';
  if (/(progress|active|ongoing|started|open|scheduled|checked out)/.test(n)) return 'blue';
  return 'gray';
}
